import { resetPasswordSchema } from "@frontend-shared/schema/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useResetPassword } from "@/api/auth/useResetPassword";
import { FormSubmitButton } from "@/components/FormSubmitButton";
import { Logo } from "@/components/Logo";
import { PasswordStrengthMeter } from "@/components/PasswordStrengthMeter";
import { StyledPasswordInput } from "@/components/StyledPasswordInput";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PATHS } from "@/constants/paths";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { stripUrlCredential } from "@/lib/strip-url-credential";
import { cn } from "@/lib/utils";
import { FORM_VALIDATION_OPTIONS } from "@/lib/validations/form-defaults";

import { CenterCardLayout } from "./CenterCardLayout";
import { AUTH_CONTROL_HEIGHT_CLASS } from "./constants/auth-controls";

const ResetPassword = () => {
  const { t } = useTranslation("auth");
  useDocumentTitle(t("page_title"));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // NOTE: NSAI-6105 — read ONCE, before the effect below removes it from the
  // URL. Reading `searchParams` on every render instead would blank the token
  // the moment the address bar is scrubbed and drop the user out of the form
  // mid-reset.
  const [token] = useState(() => searchParams.get("token"));

  useEffect(() => {
    stripUrlCredential({ parameter: "token" });
  }, []);

  const resetPasswordMutation = useResetPassword();

  const form = useForm({
    ...FORM_VALIDATION_OPTIONS,
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    resolver: zodResolver(resetPasswordSchema(t)),
  });

  const handleSubmit = form.handleSubmit((data) => {
    // NOTE: This code is unreachable because we are not rendering the ui to call this function if token is missing, but this is added for type-safety
    /* istanbul ignore next */
    if (!token) return;

    resetPasswordMutation.mutate(
      {
        token,
        password: data.password,
      },
      {
        onSuccess: () => navigate(PATHS.AUTH.SIGN_IN, { replace: true }),
      },
    );
  });

  // NOTE: NSAI-6105 — a missing token is an ordinary thing to hit now, not a
  // broken URL: the effect above strips the credential, so a reload or a Back
  // onto this entry arrives without one. A bare 404 reads as a bug to the user,
  // so say what happened and where to get a fresh link. Mirrors the invite
  // page's invalid state, which already covered the same situation.
  if (!token) {
    return (
      <CenterCardLayout>
        <div
          className="flex flex-col items-center gap-3 text-center"
          data-testid="reset-password_invalid"
        >
          <Logo
            wrapperClassName="flex justify-center"
            className="h-logo-height-auth w-auto"
          />
          <h1 className="text-xl md:text-2xl font-semibold mt-3">
            {t("reset_password.invalid_title")}
          </h1>
          <p className="text-foreground-secondary text-balance">
            {t("reset_password.invalid_description")}
          </p>
        </div>
      </CenterCardLayout>
    );
  }

  return (
    <CenterCardLayout>
      <div className="flex flex-col justify-center">
        <Logo
          wrapperClassName="flex justify-center"
          className="h-logo-height-auth w-auto"
        />
        <h1
          className="text-xl md:text-2xl lg:text-3xl font-semibold text-center mt-3 md:mt-5"
          data-testid="reset-password_title"
        >
          <Trans
            t={t}
            i18nKey="reset_password.title"
            components={{
              Highlighted: <span className="text-highlighted" />,
            }}
          />
        </h1>
        <p
          className="text-center text-foreground-secondary mt-2 md:mt-3 lg:mt-4 text-balance"
          data-testid="reset-password_description"
        >
          {t("reset_password.description")}
        </p>
      </div>
      <Form {...form}>
        <form onSubmit={handleSubmit} className="space-y-4 mt-6">
          <FormField
            control={form.control}
            name="password"
            rules={{
              deps: ["confirmPassword"],
            }}
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormLabel>{t("password.label")}</FormLabel>
                <FormControl>
                  <StyledPasswordInput
                    className={AUTH_CONTROL_HEIGHT_CLASS}
                    placeholder={t("password.placeholder")}
                    {...field}
                    autoComplete="new-password"
                    isError={!!error}
                    data-testid="reset-password_password-input"
                  />
                </FormControl>
                <PasswordStrengthMeter
                  password={field.value}
                  data-testid="reset-password_password-strength"
                />
                <FormMessage data-testid="reset-password_password-error" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormLabel>{t("confirm_password.label")}</FormLabel>
                <FormControl>
                  <StyledPasswordInput
                    className={AUTH_CONTROL_HEIGHT_CLASS}
                    placeholder={t("confirm_password.placeholder")}
                    {...field}
                    autoComplete="new-password"
                    isError={!!error}
                    data-testid="reset-password_confirm-password-input"
                  />
                </FormControl>
                <FormMessage data-testid="reset-password_confirm-password-error" />
              </FormItem>
            )}
          />
          <FormSubmitButton
            className={cn("w-full", AUTH_CONTROL_HEIGHT_CLASS)}
            isLoading={resetPasswordMutation.isPending}
            data-testid="reset-password_submit-button"
          >
            {t("reset_password.submit")}
          </FormSubmitButton>
        </form>
      </Form>
    </CenterCardLayout>
  );
};
export default ResetPassword;
